/**
 * Command Feedback (ISS-0141)
 *
 * A gamemaster command can fail after the narration describing it has already
 * been written to chat. Before this module those failures were a console.warn
 * and a bare return: the player saw an empty Campaign Map view with no reason
 * given, and the gamemaster went on referring to a region map that was never
 * created, because nothing told it the command had not landed.
 *
 * So every failure now goes two places:
 *   1. A toast, so the player knows the world did not change.
 *   2. A queue drained into the next gamemaster call, so the model finds out.
 *
 * This lives in its own module rather than in command-bridge.ts because
 * soloGamemasterStore already imports the bridge; the bridge importing the
 * store back would close a cycle.
 */

import type { CommandOutcome } from '@machina/core';
import { notify } from '../stores/notificationStore';

/**
 * Bound so a long stall (or a model that keeps reissuing a doomed command)
 * cannot grow the next prompt without limit. Oldest entries drop first.
 */
const MAX_QUEUED = 8;

let queue: CommandOutcome[] = [];

/**
 * What to tell the player when a given command type fails. Kept plain — the
 * player is here to play, not to read an error. The in-fiction version of this
 * is ISS-0137; until then a plain sentence beats a blank screen.
 */
const PLAYER_FACING: Record<string, string> = {
  setup_campaign_map: 'The region map could not be drawn.',
  generate_map: 'The battle map could not be drawn.',
  reveal_location: 'That location is not on your map.',
  move_party: 'The party could not travel there.',
  place_party: 'The party could not be placed on the map.',
  spawn_encounter: 'The encounter could not be set up.',
  sync_board: 'The board could not be updated.',
  start_combat: 'Combat could not be started.',
};

/**
 * Record a command that did not take effect.
 *
 * @param type   the GamemasterCommand.type that was attempted
 * @param detail why it failed, phrased for the gamemaster's next prompt
 * @param silent skip the toast — for failures the player has no stake in
 */
export function reportCommandFailure(type: string, detail: string, silent = false): void {
  console.warn(`[CommandBridge] ${type} did not take effect: ${detail}`);

  queue.push({ type, ok: false, detail });
  if (queue.length > MAX_QUEUED) queue = queue.slice(-MAX_QUEUED);

  if (silent) return;
  const message = PLAYER_FACING[type];
  if (!message) return;
  notify({ level: 'warning', message, detail: 'The story continues — the gamemaster has been told.' });
}

/**
 * Drain the queue for delivery to the gamemaster. Draining clears it: each
 * failure is reported to the model exactly once.
 */
export function takeCommandFeedback(): CommandOutcome[] {
  const drained = queue;
  queue = [];
  return drained;
}

/**
 * Put drained feedback back, because the call it was bound for never reached
 * the gamemaster. Without this a failed turn swallows the very report that
 * would have explained the next one.
 */
export function restoreCommandFeedback(items: CommandOutcome[]): void {
  if (items.length === 0) return;
  queue = [...items, ...queue].slice(-MAX_QUEUED);
}

/** Read the queue without draining it — for tests and debugging. */
export function peekCommandFeedback(): readonly CommandOutcome[] {
  return queue;
}
